import { ReportType } from './report'

// categories
const REPORT_PATH_CONTAINER_DETAILS = 'containerDetails'
const REPORT_PATH_SHIPMENT_DETAILS = 'shipmentDetails'
const REPORT_PATH_ROUTE = 'route'
const REPORT_PATH_TRANSPORT_MILESTONES = 'transportMilestones'
const REPORT_PATH_DEFAULT = 'default'

export type Category =
  | typeof REPORT_PATH_CONTAINER_DETAILS
  | typeof REPORT_PATH_SHIPMENT_DETAILS
  | typeof REPORT_PATH_ROUTE
  | typeof REPORT_PATH_TRANSPORT_MILESTONES
  | typeof REPORT_PATH_DEFAULT

export const reportColumnMap: Map<ColumnName, { category: Category; label: ReportColumnLabel }> = new Map([
  [
    'containerNumber',
    {
      category: REPORT_PATH_CONTAINER_DETAILS,
      label: 'container_number',
    },
  ],
  [
    'containerType',
    {
      category: REPORT_PATH_CONTAINER_DETAILS,
      label: 'container_type',
    },
  ],
  [
    'containerId',
    {
      category: REPORT_PATH_CONTAINER_DETAILS,
      label: 'container_id',
    },
  ],
  [
    'sealNumber',
    {
      category: REPORT_PATH_CONTAINER_DETAILS,
      label: 'seal_number',
    },
  ],
  [
    'actualCBM',
    {
      category: REPORT_PATH_CONTAINER_DETAILS,
      label: 'actual_cbm',
    },
  ],
  [
    'quotedCBM',
    {
      category: REPORT_PATH_CONTAINER_DETAILS,
      label: 'quoted_cbm',
    },
  ],
  [
    'actualGrossWeight',
    {
      category: REPORT_PATH_CONTAINER_DETAILS,
      label: 'actual_gross_weight',
    },
  ],
  [
    'quotedGrossWeight',
    {
      category: REPORT_PATH_CONTAINER_DETAILS,
      label: 'quoted_gross_weight',
    },
  ],
  [
    'cargoDetails',
    {
      category: REPORT_PATH_CONTAINER_DETAILS,
      label: 'cargo_details',
    },
  ],
  [
    'pricePerKg',
    {
      category: REPORT_PATH_CONTAINER_DETAILS,
      label: 'price_per_kg',
    },
  ],
  [
    'actualChargeableWeight',
    {
      category: REPORT_PATH_CONTAINER_DETAILS,
      label: 'actual_chargeable_weight',
    },
  ],
  [
    'quotedChargeableWeight',
    {
      category: REPORT_PATH_CONTAINER_DETAILS,
      label: 'quoted_chargeable_weight',
    },
  ],
  [
    'carbonEmissions',
    {
      category: REPORT_PATH_CONTAINER_DETAILS,
      label: 'carbon_emissions',
    },
  ],
  [
    'carbonOffset',
    {
      category: REPORT_PATH_CONTAINER_DETAILS,
      label: 'carbon_offset',
    },
  ],

  [
    'shipmentId',
    {
      category: REPORT_PATH_SHIPMENT_DETAILS,
      label: 'shipment_id',
    },
  ],
  [
    'shipmentStatus',
    {
      category: REPORT_PATH_SHIPMENT_DETAILS,
      label: 'shipment_status',
    },
  ],
  [
    'customerReference',
    {
      category: REPORT_PATH_SHIPMENT_DETAILS,
      label: 'customer_reference',
    },
  ],
  [
    'additionalInformation',
    {
      category: REPORT_PATH_SHIPMENT_DETAILS,
      label: 'additional_information',
    },
  ],
  [
    'transportMode',
    {
      category: REPORT_PATH_SHIPMENT_DETAILS,
      label: 'transport_mode',
    },
  ],
  [
    'transportType',
    {
      category: REPORT_PATH_SHIPMENT_DETAILS,
      label: 'transport_type',
    },
  ],
  [
    'bookingDate',
    {
      category: REPORT_PATH_SHIPMENT_DETAILS,
      label: 'booking_date',
    },
  ],
  [
    'incoterm',
    {
      category: REPORT_PATH_SHIPMENT_DETAILS,
      label: 'incoterm',
    },
  ],
  [
    'pickupCompanyName',
    {
      category: REPORT_PATH_SHIPMENT_DETAILS,
      label: 'pickup_company_name',
    },
  ],
  [
    'deliveryCompanyName',
    {
      category: REPORT_PATH_SHIPMENT_DETAILS,
      label: 'delivery_company_name',
    },
  ],
  [
    'shipperCompanyName',
    {
      category: REPORT_PATH_SHIPMENT_DETAILS,
      label: 'shipper_company_name',
    },
  ],
  [
    'consigneeCompanyName',
    {
      category: REPORT_PATH_SHIPMENT_DETAILS,
      label: 'consignee_company_name',
    },
  ],
  ['vessel', { category: REPORT_PATH_SHIPMENT_DETAILS, label: 'vessel' }],
  [
    'carrier',
    {
      category: REPORT_PATH_SHIPMENT_DETAILS,
      label: 'carrier',
    },
  ],
  [
    'hblNumber',
    {
      category: REPORT_PATH_SHIPMENT_DETAILS,
      label: 'hbl_number',
    },
  ],
  [
    'shipperContactTime',
    {
      category: REPORT_PATH_SHIPMENT_DETAILS,
      label: 'origin_information-shipper_contact_time',
    },
  ],
  [
    'carrierConfirmedTime',
    {
      category: REPORT_PATH_SHIPMENT_DETAILS,
      label: 'origin_information-carrier_confirm_time',
    },
  ],
  [
    'carrierContactTime',
    {
      category: REPORT_PATH_SHIPMENT_DETAILS,
      label: 'origin_information-carrier_contact_time',
    },
  ],
  ['originCountry', { category: REPORT_PATH_ROUTE, label: 'origin_country' }],
  [
    'originPostalCode',
    {
      category: REPORT_PATH_ROUTE,
      label: 'origin_postal_code',
    },
  ],
  ['originCity', { category: REPORT_PATH_ROUTE, label: 'origin_city' }],
  ['polCode', { category: REPORT_PATH_ROUTE, label: 'pol_code' }],
  ['polName', { category: REPORT_PATH_ROUTE, label: 'pol_name' }],
  ['polCountry', { category: REPORT_PATH_ROUTE, label: 'pol_country' }],
  ['podCode', { category: REPORT_PATH_ROUTE, label: 'pod_code' }],
  ['podName', { category: REPORT_PATH_ROUTE, label: 'pod_name' }],
  ['podCountry', { category: REPORT_PATH_ROUTE, label: 'pod_country' }],
  ['deliveryCountry', { category: REPORT_PATH_ROUTE, label: 'delivery_country' }],
  [
    'deliveryPostalCode',
    {
      category: REPORT_PATH_ROUTE,
      label: 'delivery_postal_code',
    },
  ],
  ['deliveryCity', { category: REPORT_PATH_ROUTE, label: 'delivery_city' }],
  [
    'pickupStatus',
    {
      category: REPORT_PATH_TRANSPORT_MILESTONES,
      label: 'pickup_status',
    },
  ],
  [
    'pickupInitialTime',
    {
      category: REPORT_PATH_TRANSPORT_MILESTONES,
      label: 'pickup_initial_time',
    },
  ],
  [
    'pickupUpdatedTime',
    {
      category: REPORT_PATH_TRANSPORT_MILESTONES,
      label: 'pickup_updated_time',
    },
  ],
  [
    'pickupDelay',
    {
      category: REPORT_PATH_TRANSPORT_MILESTONES,
      label: 'pickup_delay',
    },
  ],
  [
    'pickupReason',
    {
      category: REPORT_PATH_TRANSPORT_MILESTONES,
      label: 'pickup_reason_for_delay',
    },
  ],
  [
    'departureStatus',
    {
      category: REPORT_PATH_TRANSPORT_MILESTONES,
      label: 'departure_status',
    },
  ],
  [
    'departureInitialTime',
    {
      category: REPORT_PATH_TRANSPORT_MILESTONES,
      label: 'departure_initial_time',
    },
  ],
  [
    'departureUpdatedTime',
    {
      category: REPORT_PATH_TRANSPORT_MILESTONES,
      label: 'departure_updated_time',
    },
  ],
  [
    'departureActualTime',
    {
      category: REPORT_PATH_TRANSPORT_MILESTONES,
      label: 'departure_actual_time',
    },
  ],
  [
    'departureDelay',
    {
      category: REPORT_PATH_TRANSPORT_MILESTONES,
      label: 'departure_delay',
    },
  ],
  [
    'departureReason',
    {
      category: REPORT_PATH_TRANSPORT_MILESTONES,
      label: 'departure_reason_for_delay',
    },
  ],
  [
    'arrivalStatus',
    {
      category: REPORT_PATH_TRANSPORT_MILESTONES,
      label: 'arrival_status',
    },
  ],
  [
    'arrivalInitialTime',
    {
      category: REPORT_PATH_TRANSPORT_MILESTONES,
      label: 'arrival_initial_time',
    },
  ],
  [
    'arrivalUpdatedTime',
    {
      category: REPORT_PATH_TRANSPORT_MILESTONES,
      label: 'arrival_updated_time',
    },
  ],
  [
    'arrivalDelay',
    {
      category: REPORT_PATH_TRANSPORT_MILESTONES,
      label: 'arrival_delay',
    },
  ],
  [
    'arrivalActualTime',
    {
      category: REPORT_PATH_TRANSPORT_MILESTONES,
      label: 'arrival_actual_time',
    },
  ],
  [
    'arrivalReason',
    {
      category: REPORT_PATH_TRANSPORT_MILESTONES,
      label: 'arrival_reason_for_delay',
    },
  ],
  [
    'cargoReadyDateInitialTime',
    {
      category: REPORT_PATH_TRANSPORT_MILESTONES,
      label: 'cargo_ready_date_initial_time',
    },
  ],
  [
    'cargoReadyDateUpdatedTime',
    {
      category: REPORT_PATH_TRANSPORT_MILESTONES,
      label: 'cargo_ready_date_update_time',
    },
  ],
  [
    'cargoReadyDateStatus',
    {
      category: REPORT_PATH_TRANSPORT_MILESTONES,
      label: 'cargo_ready_date_status',
    },
  ],
  [
    'cargoReadyDateDelay',
    {
      category: REPORT_PATH_TRANSPORT_MILESTONES,
      label: 'cargo_ready_date_delay',
    },
  ],
  [
    'cargoReadyDateReason',
    {
      category: REPORT_PATH_TRANSPORT_MILESTONES,
      label: 'cargo_ready_date_reason',
    },
  ],
  [
    'deliveryStatus',
    {
      category: REPORT_PATH_TRANSPORT_MILESTONES,
      label: 'delivery_status',
    },
  ],
  [
    'deliveryInitialTime',
    {
      category: REPORT_PATH_TRANSPORT_MILESTONES,
      label: 'delivery_initial_time',
    },
  ],
  [
    'deliveryUpdatedTime',
    {
      category: REPORT_PATH_TRANSPORT_MILESTONES,
      label: 'delivery_updated_time',
    },
  ],
  [
    'deliveryActualTime',
    {
      category: REPORT_PATH_TRANSPORT_MILESTONES,
      label: 'delivery_actual_time',
    },
  ],
  [
    'deliveryDelay',
    {
      category: REPORT_PATH_TRANSPORT_MILESTONES,
      label: 'delivery_delay',
    },
  ],
  [
    'deliveryReason',
    {
      category: REPORT_PATH_TRANSPORT_MILESTONES,
      label: 'delivery_reason_for_delay',
    },
  ],
  [
    'containerGateOutStatus',
    {
      category: REPORT_PATH_TRANSPORT_MILESTONES,
      label: 'container_gate_out_status',
    },
  ],
  [
    'containerGateOutInitialTime',
    {
      category: REPORT_PATH_TRANSPORT_MILESTONES,
      label: 'container_gate_out_initial_time',
    },
  ],
  [
    'containerGateOutActualTime',
    {
      category: REPORT_PATH_TRANSPORT_MILESTONES,
      label: 'container_gate_out_actual_time',
    },
  ],
  [
    'containerDischargedStatus',
    {
      category: REPORT_PATH_TRANSPORT_MILESTONES,
      label: 'container_discharged_status',
    },
  ],
  [
    'containerDischargedInitialTime',
    {
      category: REPORT_PATH_TRANSPORT_MILESTONES,
      label: 'container_discharged_initial_time',
    },
  ],
  [
    'containerDischargedActualTime',
    {
      category: REPORT_PATH_TRANSPORT_MILESTONES,
      label: 'container_discharged_actual_time',
    },
  ],
  [
    'containerEmptyReturnStatus',
    {
      category: REPORT_PATH_TRANSPORT_MILESTONES,
      label: 'container_empty_return_status',
    },
  ],
  [
    'containerEmptyReturnInitialTime',
    {
      category: REPORT_PATH_TRANSPORT_MILESTONES,
      label: 'container_empty_return_initial_time',
    },
  ],
  [
    'containerEmptyReturnActualTime',
    {
      category: REPORT_PATH_TRANSPORT_MILESTONES,
      label: 'container_empty_return_actual_time',
    },
  ],
  [
    'customsStatus',
    {
      category: REPORT_PATH_TRANSPORT_MILESTONES,
      label: 'customs_status',
    },
  ],
  [
    'customsCleareanceDate',
    {
      category: REPORT_PATH_TRANSPORT_MILESTONES,
      label: 'customs_cleareance_date',
    },
  ],
  [
    'beforePickup',
    {
      category: REPORT_PATH_DEFAULT,
      label: 'kpi_before_pickup',
    },
  ],
  ['pickup', { category: REPORT_PATH_DEFAULT, label: 'kpi_pickup' }],
  ['pol', { category: REPORT_PATH_DEFAULT, label: 'kpi_pol' }],
  ['transit', { category: REPORT_PATH_DEFAULT, label: 'kpi_transit' }],
  ['pod', { category: REPORT_PATH_DEFAULT, label: 'kpi_pod' }],
  ['delivered', { category: REPORT_PATH_DEFAULT, label: 'kpi_delivered' }],
] as const)
// To memoize
export const reportColumnMapEntries = [...reportColumnMap.entries()]
export const columnLabels = reportColumnMapEntries.reduce(
  (acc, [key, value]) => {
    return { ...acc, [key]: value.label }
  },
  {} as Record<ColumnName, ReportColumnLabel>
)
export const columnNames = [
  'containerNumber',
  'containerType',
  'containerId',
  'sealNumber',
  'actualCBM',
  'quotedCBM',
  'actualGrossWeight',
  'quotedGrossWeight',
  'cargoDetails',
  'pricePerKg',
  'actualChargeableWeight',
  'quotedChargeableWeight',
  'carbonEmissions',
  'carbonOffset',
  'shipmentId',
  'shipmentStatus',
  'customerReference',
  'additionalInformation',
  'transportMode',
  'transportType',
  'bookingDate',
  'incoterm',
  'pickupCompanyName',
  'deliveryCompanyName',
  'shipperCompanyName',
  'consigneeCompanyName',
  'vessel',
  'carrier',
  'hblNumber',
  'shipperContactTime',
  'carrierConfirmedTime',
  'carrierContactTime',
  'originCountry',
  'originPostalCode',
  'originCity',
  'polCode',
  'polName',
  'polCountry',
  'podCode',
  'podName',
  'podCountry',
  'deliveryCountry',
  'deliveryPostalCode',
  'deliveryCity',
  'pickupStatus',
  'pickupInitialTime',
  'pickupUpdatedTime',
  'pickupDelay',
  'pickupReason',
  'departureStatus',
  'departureInitialTime',
  'departureUpdatedTime',
  'departureActualTime',
  'departureDelay',
  'departureReason',
  'arrivalStatus',
  'arrivalInitialTime',
  'arrivalUpdatedTime',
  'arrivalDelay',
  'arrivalActualTime',
  'arrivalReason',
  'cargoReadyDateInitialTime',
  'cargoReadyDateUpdatedTime',
  'cargoReadyDateStatus',
  'cargoReadyDateDelay',
  'cargoReadyDateReason',
  'deliveryStatus',
  'deliveryInitialTime',
  'deliveryUpdatedTime',
  'deliveryActualTime',
  'deliveryDelay',
  'deliveryReason',
  'containerGateOutStatus',
  'containerGateOutInitialTime',
  'containerGateOutActualTime',
  'containerDischargedStatus',
  'containerDischargedInitialTime',
  'containerDischargedActualTime',
  'containerEmptyReturnStatus',
  'containerEmptyReturnInitialTime',
  'containerEmptyReturnActualTime',
  'customsStatus',
  'customsCleareanceDate',
  'beforePickup',
  'pickup',
  'pol',
  'transit',
  'pod',
  'delivered',
] as const
export type ColumnName = (typeof columnNames)[number]

// Note: This is currently only used as a type
export const reportColumnLabels = {
  containerId: 'container_id',
  containerNumber: 'container_number',
  containerType: 'container_type',
  sealNumber: 'seal_number',
  actualGrossWeight: 'actual_gross_weight',
  quotedGrossWeight: 'quoted_gross_weight',
  cargoDetails: 'cargo_details',
  shipmentId: 'shipment_id',
  shipmentStatus: 'shipment_status',
  customerReference: 'customer_reference',
  additionalInformation: 'additional_information',
  transportMode: 'transport_mode',
  transportType: 'transport_type',
  bookingDate: 'booking_date',
  incoterm: 'incoterm',
  pickupCompanyName: 'pickup_company_name',
  deliveryCompanyName: 'delivery_company_name',
  shipperCompanyName: 'shipper_company_name',
  consigneeCompanyName: 'consignee_company_name',
  vessel: 'vessel',
  carrier: 'carrier',
  hblNumber: 'hbl_number',
  shipperContactTime: 'origin_information-shipper_contact_time',
  carrierConfirmedTime: 'origin_information-carrier_confirm_time',
  carrierContactTime: 'origin_information-carrier_contact_time',
  originCountry: 'origin_country',
  originPostalCode: 'origin_postal_code',
  originCity: 'origin_city',
  polCode: 'pol_code',
  polName: 'pol_name',
  polCountry: 'pol_country',
  podCode: 'pod_code',
  podName: 'pod_name',
  podCountry: 'pod_country',
  cargoReadyDateInitialTime: 'cargo_ready_date_initial_time',
  cargoReadyDateUpdatedTime: 'cargo_ready_date_update_time',
  cargoReadyDateReason: 'cargo_ready_date_reason',
  cargoReadyDateDelay: 'cargo_ready_date_delay',
  cargoReadyDateStatus: 'cargo_ready_date_status',
  deliveryCountry: 'delivery_country',
  deliveryPostalCode: 'delivery_postal_code',
  deliveryCity: 'delivery_city',
  pickupStatus: 'pickup_status',
  pickupInitialTime: 'pickup_initial_time',
  pickupUpdatedTime: 'pickup_updated_time',
  pickupDelay: 'pickup_delay',
  pickupReason: 'pickup_reason_for_delay',
  departureStatus: 'departure_status',
  departureInitialTime: 'departure_initial_time',
  departureActualTime: 'departure_actual_time',
  departureUpdatedTime: 'departure_updated_time',
  departureDelay: 'departure_delay',
  departureReason: 'departure_reason_for_delay',
  arrivalStatus: 'arrival_status',
  arrivalInitialTime: 'arrival_initial_time',
  arrivalActualTime: 'arrival_actual_time',
  arrivalUpdatedTime: 'arrival_updated_time',
  arrivalDelay: 'arrival_delay',
  arrivalReason: 'arrival_reason_for_delay',
  deliveryStatus: 'delivery_status',
  deliveryInitialTime: 'delivery_initial_time',
  deliveryUpdatedTime: 'delivery_updated_time',
  deliveryActualTime: 'delivery_actual_time',
  deliveryDelay: 'delivery_delay',
  deliveryReason: 'delivery_reason_for_delay',
  beforePickup: 'kpi_before_pickup',
  pickup: 'kpi_pickup',
  pol: 'kpi_pol',
  transit: 'kpi_transit',
  pod: 'kpi_pod',
  delivered: 'kpi_delivered',
  actualCBM: 'actual_cbm',
  quotedCBM: 'quoted_cbm',
  pricePerKg: 'price_per_kg',
  actualChargeableWeight: 'actual_chargeable_weight',
  quotedChargeableWeight: 'quoted_chargeable_weight',
  containerGateOutStatus: 'container_gate_out_status',
  containerGateOutInitialTime: 'container_gate_out_initial_time',
  containerGateOutActualTime: 'container_gate_out_actual_time',
  containerDischargedStatus: 'container_discharged_status',
  containerDischargedInitialTime: 'container_discharged_initial_time',
  containerDischargedActualTime: 'container_discharged_actual_time',
  containerEmptyReturnStatus: 'container_empty_return_status',
  containerEmptyReturnInitialTime: 'container_empty_return_initial_time',
  containerEmptyReturnActualTime: 'container_empty_return_actual_time',
  carbonOffset: 'carbon_offset',
  carbonEmissions: 'carbon_emissions',
  customsStatus: 'customs_status',
  customsCleareanceDate: 'customs_cleareance_date',
} as const
export type ReportColumnLabel = (typeof reportColumnLabels)[keyof typeof reportColumnLabels]
/**
 * If there are fields that were removed from the UI because they are no longer supported we don't want to send reports with those fields.
 * As long as they still exist in report templates in the database, we need to filter them out so they don't cause issues with column selection.
 */
export const blockList = {
  freightCost: true,
  exchangeRate: true,
  shipmentTotalCost: true,
  containerCost: true,
} as const
export const isAllowedField = (field: string) => !(blockList as Record<string, boolean>)[field]

type ReportTypeConfig = {
  forcedSelections: Partial<Record<ColumnName, boolean>>
  allowedCategories: Category[]
}
type ReportTemplateConfig = Record<ReportType, ReportTypeConfig>
export const reportTemplateConfig: ReportTemplateConfig = {
  kpi: {
    forcedSelections: { transit: true },
    allowedCategories: [REPORT_PATH_DEFAULT],
  },
  container: {
    forcedSelections: { containerNumber: true },
    allowedCategories: [
      REPORT_PATH_CONTAINER_DETAILS,
      REPORT_PATH_SHIPMENT_DETAILS,
      REPORT_PATH_ROUTE,
      REPORT_PATH_TRANSPORT_MILESTONES,
    ],
  },
  emissions: {
    // No column selection for emissions report
    forcedSelections: {},
    allowedCategories: [],
  },
} as const
const getColumnNamesForReportType = (type: ReportType) => {
  const config = reportTemplateConfig[type]
  return columnNames.filter((name) => {
    const column = reportColumnMap.get(name)
    return column && config.allowedCategories.includes(column.category)
  })
}
const getColumnNamesForCategory = (category: Category) => {
  return columnNames.filter((name) => {
    const column = reportColumnMap.get(name)
    return column && column.category === category
  })
}
export const columnNamesForType = {
  container: getColumnNamesForReportType('container'),
  kpi: getColumnNamesForReportType('kpi'),
  emissions: getColumnNamesForReportType('emissions'),
} as const
export const columnNamesForCategory: Record<Category, ColumnName[]> = {
  [REPORT_PATH_CONTAINER_DETAILS]: getColumnNamesForCategory(REPORT_PATH_CONTAINER_DETAILS),
  [REPORT_PATH_SHIPMENT_DETAILS]: getColumnNamesForCategory(REPORT_PATH_SHIPMENT_DETAILS),
  [REPORT_PATH_ROUTE]: getColumnNamesForCategory(REPORT_PATH_ROUTE),
  [REPORT_PATH_TRANSPORT_MILESTONES]: getColumnNamesForCategory(REPORT_PATH_TRANSPORT_MILESTONES),
  [REPORT_PATH_DEFAULT]: getColumnNamesForCategory(REPORT_PATH_DEFAULT),
} as const
